/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { AiOutlineSearch } from "react-icons/ai";

import {
  ActionIcon,
  Badge,
  Button,
  Input,
  Pagination,
  Select,
  Skeleton,
  Table,
  Text,
  CloseButton,
  Modal,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Eye } from "tabler-icons-react";
import { format, formatISO, parseISO } from "date-fns";
import { useDebouncedValue } from "@mantine/hooks";
import {
  getValueForInput,
  loadingStates,
  csvDownload,
  formatDate,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  downloadRenewalCSV,
  getAgreementInRenewalsList,
  getAgreementTypesApi,
  getAgreementsCsvDownload,
  updateRenewalStatus,
} from "../../utilities/apis/agreements";
import { agreementStatusColors } from "../../utilities/enums";
import Styles from "./AgreementsListingPage.module.css";
import HamburgerMenuAgreements from "../HamburgerMenuAgreements";
import UniversalTable from "../../components/Common/UniversalTable";
import TablePagination from "../../components/Common/TablePagination";

const statusOptions = [
  {
    label: "Pending with Company",
    value: "Pending with Company",
  },
  {
    label: "TP",
    value: "TP",
  },
];

const AgreementsExpired = () => {
  const [tabName, setTabName] = useState("renewals");
  const [pageRenewals, setPageRenewals] = useState(1);
  const [pageExpired, setPageExpired] = useState(1);
  const [configRenewals, setConfigRenewals] = useState({
    loading: loadingStates.LOADING,
    data: null,
  });
  let mainData;
  const [configExpired, setConfigExpired] = useState({
    loading: loadingStates.LOADING,
    data: null,
  });
  const activePage = tabName === "renewals" ? pageRenewals : pageExpired;
  const [pageSize, setPageize] = useState(10);

  const fetchExpired = async () => {
    const response = await apiWrapWithErrorWithData(
      getAgreementInRenewalsList({ page: pageExpired, tabName: "expired" })
    );
    setConfigExpired({
      loading: loadingStates.NO_ACTIVE_REQUEST,
      data: response,
    });
    console.log("expired: ", response);
  };
  useEffect(() => {
    fetchExpired();
  }, [pageExpired]);

  const fetchRenewals = async () => {
    const response = await apiWrapWithErrorWithData(
      getAgreementInRenewalsList({ page: pageRenewals, tabName: "renewals" })
    );
    setConfigRenewals({
      loading: loadingStates.NO_ACTIVE_REQUEST,
      data: response,
    });
    console.log("renewals: ", response);
  };
  useEffect(() => {
    fetchRenewals();
  }, [pageRenewals]);
  useEffect(() => {
    if (tabName === "renewals") {
      mainData = configRenewals;
    } else if (tabName === "expired") {
      mainData = configExpired;
    }
  }, [tabName, configExpired, configRenewals]);

  if (tabName === "renewals") {
    mainData = configRenewals;
  } else if (tabName === "expired") {
    mainData = configExpired;
  }
  const navigate = useNavigate();
  const onClick = (agreementId) => {
    const url = `/app/agreements_manager/agreements/details/${agreementId}`;
    window.open(url, "_blank");
  };
  let srNo;
  if (tabName === "expired") {
    srNo = (pageExpired - 1) * 10 + 1;
  }
  if (tabName === "renewals") {
    srNo = (pageRenewals - 1) * 10 + 1;
  }

  function convertToISOFormat(dateStr) {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  }
  const onViewClick = (agreementId) => {
    const url = `/app/agreements_manager/agreements/details/${agreementId}`;
    window.open(url, "_blank"); // '_blank' specifically tells the browser to open in a new tab/window
  };
  function getDaysDifference(targetDate) {
    const currentDate = new Date();
    const target = new Date(targetDate);
    const timeDifference = target - currentDate;
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    return daysDifference;
  }

  const handleCsvDownload = async () => {
    const response = await apiWrapWithErrorWithData(
      downloadRenewalCSV({ tabName })
    );
    if (response.success) {
      const columnHeaders = Object.keys(response.agreements[0]);
      const agreements = response.agreements;
      const transformedAgreements = agreements.map(
        ({ secondParty, dueDateOfContract, reminderTime, ...rest }) => ({
          ...rest,
          secondParty: `${secondParty.brandName}/${secondParty.name}`,
          dueDateOfContract: parseISO(dueDateOfContract),
          reminderTime: parseISO(reminderTime),
        })
      );
      console.log("agreements: ", agreements);
      csvDownload({
        data: transformedAgreements,
        headers: columnHeaders,
        name: "agreement",
      });
    }
  };
  const [actionModal, setActionModal] = useState(false);
  const [actionId, setActionId] = useState(null);

  const handleAgreementRenewal = async () => {
    const response = await apiWrapWithErrorWithData(
      updateRenewalStatus({ id: actionId, status: "RENEWED" })
    );
    if (response.success) {
      navigate(`/app/agreements_manager/agreements/new?ag=${actionId}`);
    }
  };

  const handleAgreementClose = async () => {
    const response = await apiWrapWithErrorWithData(
      updateRenewalStatus({ id: actionId, status: "CLOSED" })
    );
    if (response.success) {
      window.location.reload();
    }
  };

  const tableHeaders = [
    {
      label: "NO.",
      render: (_, index) => (activePage - 1) * pageSize + (index + 1),
      width: "70px"
    },
    { label: "CIN", render: (row) => <Text size="sm" weight={600} color="#43A3B3">#{row.id}</Text>  },
    { label: "type", render: (row) => row.agreementData?.json?.typeOfAgreement || "-" },
    { label: "BU", render: (row) => row.agreementData?.json?.location || "-" },
    { label: "second party", render: (row) => row.agreementData?.json?.secondParty?.name || "-" },
    { label: "start date", render: (row) => formatDate(row.contractStartDate) || "-" },
    { label: "end date", render: (row) => formatDate(row.contractEndDate) || "-" },
    ...(tabName === "renewals" ? [{ 
        label: "due days", 
        render: (row) => <Text color="orange" weight={600}>{getDaysDifference(row.contractEndDate)} days</Text> 
    }] : []),

    {
      label: "actions",
      align: "right",
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {/* View Icon */}
          <ActionIcon onClick={() => onViewClick(row.id)} variant="subtle">
            <Eye color="#1b6f8d" size={20} />
          </ActionIcon>
          
          {/* The Action Button that opens the Modal */}
          <Button 
            size="xs" 
            variant="filled" 
            style={{ backgroundColor: "#1b6f8d" }}
            onClick={() => {
              setActionId(row.id);    // Set the ID for the modal actions
              setActionModal(true);   // Open the modal
            }}
          >
            Action
          </Button>
        </div>
      )
    }
  ];
  return (
    <>
      <div
        className="flex"
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "600",
            letterSpacing: "1px",
            textTransform: "lowercase",
            fontFamily: "Lato",
            whiteSpace: "nowrap",
            paddingBottom: "13px",
            position: "relative",
            color: "black",
          }}
        >
          Renewals/Expiration
        </h1>

        <HamburgerMenuAgreements />
      </div>
      <div className="flex justify-around items-center mt-4 hover:text-co">
        <Button
          className="hover:bg-blue-500 hover:text-white"
          variant="outline"
          color="#1b6f8d"
          onClick={() => setTabName("renewals")}
        >
          Renewals
        </Button>
        <Button
          className="hover:bg-red-500 hover:text-white"
          variant="outline"
          onClick={() => setTabName("expired")}
        >
          Expired
        </Button>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-between my-4">
          <Text>{tabName === "renewals" ? "expiring soon" : "expired"}</Text>
          <Button
            className="mx-2 btn btn-success btn-lg float-right"
            // disabled={exportButtonLoading}
            onClick={handleCsvDownload}
            // color={themeColor(colors.agreement)}
            style={{
              backgroundColor: "#1b6f8d",
            }}
          >
            Export
          </Button>
        </div>
      </div>

      {mainData.loading === loadingStates.LOADING && (
        <div className="flex flex-col">
          {Array.from({ length: 10 }).map((i) => (
            <Skeleton key={i} height={30} radius="md" className="my-1 w-full" />
          ))}
          <div className="flex flex-row justify-center">
            <Skeleton height={40} radius="md" className="w-40" />
          </div>
        </div>
      )}
      {mainData.loading === loadingStates.NO_ACTIVE_REQUEST &&
        Boolean(mainData.data?.agreementsCount) && (
          <div>
            <UniversalTable
              headers={tableHeaders}
              data={mainData.data?.agreements || []}
              loading={mainData.loading === loadingStates.LOADING}
              emptyMessage={`No ${tabName} found.`}
            />

            <TablePagination
              totalCount={mainData.data?.agreementsCount || 0}
              page={activePage}
              pageSize={pageSize}
              dataLength={mainData.data?.agreements?.length || 0}
              onPageChange={(p) =>
                tabName === "renewals" ? setPageRenewals(p) : setPageExpired(p)
              }
              onPageSizeChange={() => {}}
            />
          </div>
        )}
      <Modal
        opened={actionModal}
        title="take action"
        onClose={() => setActionModal(false)}
      >
        <div className="flex justify-between">
          <Button onClick={handleAgreementClose}>don't renew</Button>
          <Button onClick={handleAgreementRenewal} variant="outline">
            Renew
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AgreementsExpired;
